import React, { useMemo } from "react";
import MobileValueCard from "./MobileValueCard";
import { Box, Typography, Button, Tooltip, IconButton } from "@mui/material";
import moment from "moment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import { Link } from "react-router-dom";
import { formatRate, getBookieLogoUrl } from "../../constants";
import {
  numberFilterOperators,
  simplifiedStringFilterOperators,
  createMarketOperators,
  rateFilterOperators,
  dateFilterOperators,
} from "../Filters";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { calculateKellyStake } from "../../utils/kelly";
import ToggleableDate from "../ToggleableDate";

function formatToThreeDigits(number) {
  const absNumber = Math.abs(number);
  let formattedNumber;

  if (absNumber >= 100) {
    formattedNumber = Math.round(absNumber).toString();
  } else if (absNumber >= 10) {
    formattedNumber = absNumber.toFixed(1);
  } else {
    formattedNumber = absNumber.toFixed(2);
  }

  return number < 0 ? "-" + formattedNumber : formattedNumber;
}

const renderOdds = (
  value,
  isPercentage = false,
  isNumber = false,
  showInfoIcon = false,
  threeDecimals = false
) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      height: "100%",
    }}
  >
    <Typography
      variant="subTitle1"
      sx={{
        padding: { xs: "4px", sm: "8px" },
        borderRadius: "4px",
        minWidth: "60px",
        fontWeight: 600,
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        lineHeight: "18px",
        backgroundColor: (theme) => theme.palette.eventBackground.main,
      }}
    >
      {isPercentage
        ? `${formatToThreeDigits(value)}%`
        : isNumber
        ? value
        : formatRate(value, threeDecimals, true)}
      {showInfoIcon && (
        <InfoOutlinedIcon
          sx={{
            fontSize: 16,
            opacity: 0.7,
            color: "primary.main",
          }}
        />
      )}
    </Typography>
  </Box>
);

const ValuePageColumns = ({
  columnWidths,
  handleColumnWidthChange,
  columnVisibilityModel,
  copyToBetTracker,
  isMobile,
  kellySettings,
  hideBet,
  handleGroupedBetClick,
  evMode,
  columnOrder,
  marketOptions,
}) => {
  const formatDollarAmount = (amount) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const renderKellyStake = (params) => {
    const { rate, fairOdds } = params.row;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          height: "100%",
        }}
      >
        <Typography
          variant="subTitle1"
          sx={{
            padding: { xs: "4px", sm: "8px" },
            borderRadius: "4px",
            minWidth: "60px",
            fontWeight: 600,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            lineHeight: "18px",
            backgroundColor: (theme) => theme.palette.eventBackground.main,
            cursor: "pointer",
          }}
        >
          {calculateKellyStake(params.value, rate, fairOdds, kellySettings)}
        </Typography>
      </Box>
    );
  };

  const baseColumns = [
    {
      field: "eventStart",
      headerName: "Date",
      width: columnWidths["eventStart"] || 200,
      headerAlign: "left",
      align: "left",
      filterOperators: dateFilterOperators,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Tooltip title="Hide Bet">
              <IconButton onClick={() => hideBet(params.row)} size="small">
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy to Bet Tracker">
              <IconButton onClick={() => copyToBetTracker(params)} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <ToggleableDate date={params.value} />
          </Box>
        );
      },
    },
    {
      field: "key",
      headerName: "Bet",
      width: columnWidths["key"] || 250,
      headerAlign: "left",
      align: "left",
      filterOperators: simplifiedStringFilterOperators,
    },
    {
      field: "market",
      headerName: "Market",
      width: columnWidths["market"] || 250,
      headerAlign: "left",
      align: "left",
      filterOperators: [...createMarketOperators(marketOptions), ...simplifiedStringFilterOperators],
    },
    {
      field: "league",
      headerName: "League",
      width: columnWidths["league"] || 250,
      headerAlign: "left",
      align: "left",
      filterOperators: simplifiedStringFilterOperators,
    },
    {
      field: "event",
      headerName: "Event",
      width: columnWidths["event"] || 350,
      headerAlign: "left",
      align: "left",
      filterOperators: simplifiedStringFilterOperators,
      renderCell: (params) => {
        if (!params.value) return null;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Button
              component={Link}
              to={`/event/${params.row.event_id}?market=${encodeURIComponent(
                params.row.market
              )}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
                p: 0,
                width: "max-content",
                textTransform: "none",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <LinkIcon />
              {params.row.event}
            </Button>
          </Box>
        );
      },
    },
    {
      field: "bookie",
      headerName: "Bookie",
      width: columnWidths["bookie"] || 170,
      headerAlign: "left",
      align: "left",
      filterOperators: simplifiedStringFilterOperators,
      renderCell: (params) => {
        if (!params.value) return null;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
            }}
            onClick={() => window.open(params.row.source)}
          >
            <img
              src={getBookieLogoUrl(params.value)}
              alt={params.value}
              style={{
                borderRadius: "12px",
                width: "160px",
                height: "40px",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "bookieCount",
      headerName: "Count",
      renderHeader: () => (
        <Tooltip
          title="The number of alternative bookies used to calculate fair odds"
          placement="top"
        >
          <span>Count</span>
        </Tooltip>
      ),
      width: columnWidths["bookieCount"] || 100,
      headerAlign: "left",
      align: "left",
      type: "number",
      renderCell: (params) => (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            handleGroupedBetClick(params.row);
          }}
          sx={{
            cursor: "pointer",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          {renderOdds(params.value, false, true, true)}
        </Box>
      ),
      filterOperators: numberFilterOperators,
    },
    {
      field: "rate",
      headerName: "Odds",
      renderHeader: () => (
        <Tooltip title="The odds at the listed bookie" placement="top">
          <span>Odds</span>
        </Tooltip>
      ),
      width: columnWidths["rate"] || 80,
      headerAlign: "left",
      align: "left",
      type: "number",
      renderCell: (params) => renderOdds(params.value),
      filterOperators: rateFilterOperators,
    },
    {
      field: "bestAltOdds",
      headerName: "Best Alt",
      renderHeader: () => (
        <Tooltip
          title="The best alternative odds available at other bookies"
          placement="top"
        >
          <span>Best Alt</span>
        </Tooltip>
      ),
      width: columnWidths["bestAltOdds"] || 80,
      headerAlign: "left",
      align: "left",
      type: "number",
      renderCell: (params) => (
        <Tooltip
          title={
            params.row.bestAltBookie ? (
              <Box sx={{ p: 0.5 }}>
                <img
                  src={getBookieLogoUrl(params.row.bestAltBookie)}
                  alt={params.row.bestAltBookie}
                  style={{
                    borderRadius: "8px",
                    width: "120px",
                    height: "30px",
                  }}
                />
              </Box>
            ) : (
              "No alternative bookie"
            )
          }
          placement="top"
        >
          <Box sx={{ cursor: "pointer" }}>{renderOdds(params.value)}</Box>
        </Tooltip>
      ),
      filterOperators: numberFilterOperators,
    },
    {
      field: "pinnacleFairOdds",
      headerName: "Pinnacle",
      renderHeader: () => (
        <Tooltip
          title={evMode
            ? "De-vigged Pinnacle odds. A great indicator of true odds, esp. on US sports."
            : "Pinnacle odds. A great indicator of true odds, esp. on US sports."}
          placement="top"
        >
          <span>Pinnacle</span>
        </Tooltip>
      ),
      width: columnWidths["pinnacleOdds"] || 80,
      headerAlign: "left",
      align: "left",
      type: "number",
      renderCell: (params) =>
        renderOdds(
          params.value > 0 ? params.value : "N/A",
          false,
          !params.value > 0
        ),
      filterOperators: numberFilterOperators,
    },
    {
      field: "aveOdds",
      headerName: "Avg Odds",
      renderHeader: () => (
        <Tooltip
          title="The average odds across all bookies. Bookies that share odds are only counted once."
          placement="top"
        >
          <span>Avg Odds</span>
        </Tooltip>
      ),
      width: columnWidths["aveOdds"] || 80,
      headerAlign: "left",
      align: "left",
      type: "number",
      renderCell: (params) => renderOdds(params.value),
      filterOperators: numberFilterOperators,
    },
    evMode && {
      field: "fairOdds",
      headerName: "Fair Odds",
      width: columnWidths["fairOdds"] || 100,
      headerAlign: "left",
      align: "left",
      renderHeader: () => (
        <Tooltip
          title="The devigged average odds across all bookies. Bookies that share odds are only counted once."
          placement="top"
        >
          <span>Fair Odds</span>
        </Tooltip>
      ),
      renderCell: (params) => (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            handleGroupedBetClick(params.row);
          }}
          sx={{
            cursor: "pointer",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          {renderOdds(params.value, false, false, false, true)}
        </Box>
      ),
      filterOperators: rateFilterOperators,
    },
    {
      field: "oddsAveRatio",
      headerName: "Odds/Avg",
      renderHeader: () => (
        <Tooltip
          title="The percentage difference between the Odds and the Average Odds"
          placement="top"
        >
          <span>Odds/Avg</span>
        </Tooltip>
      ),
      width: columnWidths["oddsAveRatio"] || 100,
      headerAlign: "left",
      align: "left",
      type: "number",
      valueGetter: (value) => (value - 1) * 100,
      renderCell: (params) => renderOdds(params.value, true),
      filterOperators: numberFilterOperators,
    },
    {
      field: "firstSecondRatio",
      headerName: "Odds/Alt",
      renderHeader: () => (
        <Tooltip
          title="The percentage difference between the Odds and the Best Alternative"
          placement="top"
        >
          <span>Odds/Alt</span>
        </Tooltip>
      ),
      width: columnWidths["firstSecondRatio"] || 100,
      headerAlign: "left",
      align: "left",
      type: "number",
      valueGetter: (value) => (value - 1) * 100,
      renderCell: (params) => renderOdds(params.value, true),
      filterOperators: numberFilterOperators,
    },
    evMode && {
      field: "oddsFairRatio",
      headerName: "Odds/Fair",
      renderHeader: () => (
        <Tooltip
          title="The percentage difference between the Odds and the Fair Odds"
          placement="top"
        >
          <span>Odds/Fair</span>
        </Tooltip>
      ),
      width: columnWidths["oddsFairRatio"] || 100,
      headerAlign: "left",
      align: "left",
      type: "number",
      valueGetter: (value) => (value - 1) * 100,
      renderCell: (params) => renderOdds(params.value, true),
      filterOperators: numberFilterOperators,
    },
    evMode && {
      field: "ev",
      headerName: "EV",
      renderHeader: () => (
        <Tooltip
          title="The expected value of this bet assuming the fair odds are correct. The higher the bookie count, the more accurate this will be."
          placement="top"
        >
          <span style={{ paddingRight: "20px" }}>EV</span>
        </Tooltip>
      ),
      width: columnWidths["ev"] || 100,
      headerAlign: "left",
      align: "left",
      type: "number",
      renderCell: (params) => {
        const value = params.value;
        let color, backgroundColor;
        if (value >= 3) {
          color = "#35BD5B";
          backgroundColor = "rgba(53, 189, 91, 0.1)";
        } else if (value > 0 && value < 3) {
          color = "#FFA500";
          backgroundColor = "rgba(255, 165, 0, 0.1)";
        } else {
          color = "#FF0000";
          backgroundColor = "rgba(255, 0, 0, 0.1)";
        }
        return (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: { xs: "4px", sm: "8px" },
                borderRadius: "4px",
                border: `2px solid ${color}`,
                backgroundColor: backgroundColor,
                width: "100%",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {`${value.toFixed(2)}%`}
              </Typography>
            </Box>
          </Box>
        );
      },
      filterOperators: numberFilterOperators,
    },
    evMode && {
      field: "quarterKelly",
      headerName: "1/4 Kelly",
      width: columnWidths["quarterKelly"] || 100,
      headerAlign: "left",
      align: "left",
      renderHeader: () => (
        <Tooltip title="1/4 Kelly stake" placement="top">
          <span style={{ display: "flex", alignItems: "center" }}>
            1/4 Kelly
          </span>
        </Tooltip>
      ),
      valueGetter: (params) => 25,
      renderCell: renderKellyStake,
      filterOperators: numberFilterOperators,
    },
    evMode && {
      field: "halfKelly",
      headerName: "1/2 Kelly",
      width: columnWidths["halfKelly"] || 100,
      headerAlign: "left",
      align: "left",
      renderHeader: () => (
        <Tooltip title="Half Kelly stake" placement="top">
          <span style={{ display: "flex", alignItems: "center" }}>
            Half Kelly
          </span>
        </Tooltip>
      ),
      valueGetter: (params) => 50,
      renderCell: renderKellyStake,
      filterOperators: numberFilterOperators,
    },
    evMode && {
      field: "fullKelly",
      headerName: "Kelly",
      width: columnWidths["fullKelly"] || 100,
      headerAlign: "left",
      align: "left",
      renderHeader: () => (
        <Tooltip title="Full Kelly stake" placement="top">
          <span style={{ display: "flex", alignItems: "center" }}>
            Full Kelly
          </span>
        </Tooltip>
      ),
      valueGetter: (params) => 100,
      renderCell: renderKellyStake,
      filterOperators: numberFilterOperators,
    },
  ].filter(Boolean); // Filter out false values from conditional columns

  const mobileColumn = {
    field: "mobileCard",
    headerName: "Value Bet",
    width: "100%",
    flex: 1,
    renderCell: (params) => (
      <MobileValueCard
        row={params.row}
        columnVisibilityModel={columnVisibilityModel}
        copyToBetTracker={copyToBetTracker}
        kellySettings={kellySettings}
        calculateKellyStake={calculateKellyStake}
        hideBet={() => hideBet(params.row)}
        handleGroupedBetClick={handleGroupedBetClick}
        evMode={evMode}
      />
    ),
    sortable: false,
    filterable: false,
  };

  const desktopColumns = baseColumns.map((column) => ({
    ...column,
    width: columnWidths[column.field] || column.width,
    onColumnWidthChange: handleColumnWidthChange,
  }));

  // Sort columns based on stored order
  const sortedDesktopColumns = [...desktopColumns].sort((a, b) => {
    const orderA = columnOrder[a.field] ?? Number.MAX_SAFE_INTEGER;
    const orderB = columnOrder[b.field] ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });

  // For mobile, include all columns but only render the mobile card
  const mobileColumns = isMobile
    ? [
        mobileColumn,
        ...sortedDesktopColumns.map((col) => ({
          ...col,
          renderCell: undefined,
        })),
      ]
    : sortedDesktopColumns;

  return { mobileColumns, desktopColumns: sortedDesktopColumns };
};

export default ValuePageColumns;
